import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, Injector, model, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl } from '@angular/forms';

// translate service
import { TranslateService } from '@ngx-translate/core';

import { DynamicFormField } from '../../../models/dynamic-form';
import { DynamicFieldLabel } from '../dynamic-field-label/dynamic-field-label';
import { DynamicFieldDatepicker } from '../dynamic-field-datepicker/dynamic-field-datepicker';
import { DynamicFieldInput } from '../dynamic-field-input/dynamic-field-input';
import { DynamicFieldSelect } from '../dynamic-field-select/dynamic-field-select';
import { DynamicFieldRadioButtons } from '../dynamic-field-radio-buttons/dynamic-field-radio-buttons';
import { DynamicFieldCheckbox } from '../dynamic-field-checkbox/dynamic-field-checkbox';
import { DynamicFieldTextarea } from '../dynamic-field-textarea/dynamic-field-textarea';
import { DynamicFieldSpecialControl } from '../dynamic-field-special-control/dynamic-field-special-control';
import { DynamicHost } from '../../../directives/dynamic-host';


const componentMap: Record<string, any> = {
  ['label']: DynamicFieldLabel,
  ['date-picker']: DynamicFieldDatepicker,
  ['input']: DynamicFieldInput,
  ['select']: DynamicFieldSelect,
  ['radio-button']: DynamicFieldRadioButtons,
  ['checkbox']: DynamicFieldCheckbox,
  ['textarea']: DynamicFieldTextarea,
  ['special-control']: DynamicFieldSpecialControl,
};


@Component( {
  selector: 'app-dynamic-field',
  imports: [DynamicHost],
  templateUrl: './dynamic-field.html',
  styleUrl: './dynamic-field.css',
  changeDetection: ChangeDetectionStrategy.OnPush
} )
export class DynamicField implements AfterViewInit
{
  constructor(
    private injector: Injector,
    private _translateService: TranslateService,
    private _cdr: ChangeDetectorRef )
  {
    this.currentLanguage = this._translateService.getCurrentLang() || 'en';
    this._translateService.onLangChange.subscribe( async (lang) =>
    {
      console.log( 'Language changed to ', lang.lang );
      this.currentLanguage = lang.lang;
      await this.loadDynamicFieldComponent();
    } );
  }

  @ViewChildren( DynamicHost ) dynamicHosts!: QueryList<DynamicHost>;

  public field = model.required<DynamicFormField>();
  public control = model.required<AbstractControl>();
  public currentLanguage = 'en';

  async ngAfterViewInit(): Promise<void>
  {
    await this.loadDynamicFieldComponent();
  }

  async loadDynamicFieldComponent(): Promise<void>
  {
    this._cdr.detectChanges();

    if ( !this.dynamicHosts || this.dynamicHosts.length === 0 ) return;

    this.dynamicHosts.forEach( host =>
    {
      host.viewContainerRef.clear();
      
      const fieldType = this.field().renderType.element!;
      const componentType = componentMap[fieldType];
      if ( !componentType ) return;
      const componentRef: ComponentRef<any> = host.viewContainerRef.createComponent( componentType, {
        injector: this.injector
      } );
      
      if ( componentRef.instance.hasOwnProperty( 'type' ) )
      {
        componentRef.instance.type = this.field().renderType.type;
      }
      if ( componentRef.instance.hasOwnProperty( 'label' ) )
      {
        const labelObj = this.field().label!;
        const labelText = labelObj[this.currentLanguage as keyof typeof labelObj] || labelObj['en'] || '';
        componentRef.instance.label = labelText;
      }
      if ( componentRef.instance.hasOwnProperty( 'classes' ) )
      {
        componentRef.instance.classes = this.field().renderType.classes;
      }
      if ( componentRef.instance.hasOwnProperty( 'field' ) )
      {
        componentRef.instance.field = this.field;
      }
      if ( componentRef.instance.hasOwnProperty( 'control' ) )
      {        
        componentRef.instance.control = this.control;
      }

      componentRef.changeDetectorRef.detectChanges();
    } );

  }

}

